import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, ChevronLeft, ChevronRight, Clock, User, AlertTriangle, Check, CheckCircle2 } from "lucide-react";
import { matchInfo } from "@/components/WorkPrefsEditor";
import { UkDateInput } from "@/components/UkDateInput";
import { BundleBookForm } from "@/components/Bundles";
import { statusTone, HeaderAccent, clientTag } from "@/lib/status";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const VISIT_SERVICES = ["Companionship visits", "Extended companion visit (2 hours)", "Weekly companionship bundle", "Respite befriending", "Respite (2 hours)", "Respite (3 hours)", "Respite bundle", "Care home befriending", "Hospital befriending", "Telephone companion"];
const DEFAULT_TASKS = ["Companionship & conversation", "Walk or outing", "Shopping & errands", "Café or lunch trip", "Appointment escort", "Telephone / video check-in", "Social outing / activity", "Light housekeeping", "Wellbeing & independent-living check"];

function Avatar({ src, name, size = 20 }) {
  return src
    ? <img src={src} alt={name} className="shrink-0 rounded-full object-cover ring-1 ring-black/5" style={{ width: size, height: size }} />
    : <span className="flex shrink-0 items-center justify-center rounded-full bg-brand-light font-bold text-brand" style={{ width: size, height: size, fontSize: size * 0.42 }}>{(name || "?").charAt(0)}</span>;
}

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
const iso = (d) => d.toISOString().slice(0, 10);

export default function Roster() {
  const { user } = useAuth();
  const canManage = ["admin", "office"].includes(user?.role);
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [bundleOpen, setBundleOpen] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const load = () => {
    const from = iso(days[0]); const to = iso(days[6]);
    api.get(`/visits?date_from=${from}&date_to=${to}`).then((r) => setVisits(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);
  useEffect(() => {
    if (canManage) {
      api.get("/clients").then((r) => setClients(r.data));
      api.get("/staff").then((r) => setStaff(r.data.filter((s) => s.role === "carer")));
    }
  }, [canManage]);

  const byDay = (d) => visits.filter((v) => v.date === iso(d)).sort((a, b) => a.start_time.localeCompare(b.start_time));

  const photoMap = useMemo(() => {
    const m = {};
    staff.forEach((s) => { if (s.photo) m[s.id] = s.photo; });
    if (user?.id && user?.photo) m[user.id] = user.photo;
    return m;
  }, [staff, user]);

  const isAdmin = user?.role === "admin";
  const [grace, setGrace] = useState(null);
  useEffect(() => { if (canManage) api.get("/settings").then((r) => setGrace(r.data.missed_grace_min)).catch(() => {}); }, [canManage]);
  const saveGrace = (val) => {
    setGrace(val);
    api.put("/settings", { missed_grace_min: val }).then(() => toast.success(`Missed-visit grace set to ${val} min`)).catch((e) => toast.error(apiError(e)));
  };

  return (
    <div className="space-y-6" data-testid="roster-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">{canManage ? "Rostering" : "My visits"}</h1>
          <p className="mt-1 text-ink-soft">Week of {days[0].toLocaleDateString("en-GB", { day: "numeric", month: "long" })}</p>
          <HeaderAccent />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(n.getDate() - 7); return n; })}
            data-testid="prev-week-button" className="rounded-lg border border-border bg-white p-2.5 text-ink hover:bg-secondary"><ChevronLeft size={18} /></button>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-medium text-ink hover:bg-secondary">Today</button>
          <button onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(n.getDate() + 7); return n; })}
            data-testid="next-week-button" className="rounded-lg border border-border bg-white p-2.5 text-ink hover:bg-secondary"><ChevronRight size={18} /></button>
          {canManage && (
            <button onClick={() => setBundleOpen(true)} data-testid="book-bundle-button"
              className="ml-2 inline-flex items-center gap-2 rounded-lg border border-brand bg-white px-4 py-2.5 font-semibold text-brand hover:bg-brand-light">
              Book bundle
            </button>
          )}
          {canManage && (
            <button onClick={() => setOpen(true)} data-testid="add-visit-button"
              className="ml-2 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
              <Plus size={18} /> Schedule
            </button>
          )}
        </div>
      </div>

      {isAdmin && grace !== null && (
        <div data-testid="grace-setting" className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-white p-4 shadow-sm">
          <AlertTriangle size={16} className="shrink-0 text-gold-dark" />
          <span className="text-sm font-medium text-ink">Flag a visit as <b>missed</b> if there's no check-in</span>
          <select data-testid="grace-select" value={grace} onChange={(e) => saveGrace(Number(e.target.value))}
            className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20">
            {[5, 10, 15, 30, 45, 60].map((n) => <option key={n} value={n}>{`${n} minutes after start`}</option>)}
          </select>
          <span className="text-xs text-ink-muted">The office is alerted the moment a visit passes this window.</span>
        </div>
      )}

      {canManage && staff.length > 0 && (
        <div data-testid="team-availability" className="rounded-xl border border-border bg-white p-4 shadow-sm">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Clock size={13} /> Team availability this week</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {staff.map((s) => {
              const avail = (s.availability || []).filter((a) => a.available).map((a) => a.day);
              const blocked = (s.blocked_dates || []).filter((bd) => days.some((d) => iso(d) === bd));
              return (
                <div key={s.id} data-testid={`team-avail-${s.id}`} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <Avatar src={s.photo} name={s.name} size={28} />
                      <p className="truncate font-semibold text-ink">{s.name}</p>
                    </div>
                    {s.contracted_hours != null && <span className="shrink-0 text-[11px] text-ink-muted">{s.contracted_hours}h/wk</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {avail.length === 0
                      ? <span className="text-xs text-ink-muted">No weekly availability set</span>
                      : avail.map((d) => <span key={d} className="rounded-full bg-sage-light px-2 py-0.5 text-[11px] font-medium text-sage-dark">{d}</span>)}
                  </div>
                  {blocked.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      <span className="text-[11px] font-semibold text-coral-dark">Blocked:</span>
                      {blocked.map((bd) => <span key={bd} className="rounded-full bg-coral-light px-2 py-0.5 text-[11px] font-medium text-coral-dark">{new Date(bd).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
        {days.map((d) => {
          const today = iso(d) === iso(new Date());
          return (
            <div key={iso(d)} className="rounded-xl border border-border bg-white p-3 shadow-sm">
              <div className={`mb-3 rounded-lg px-2 py-1.5 text-center ${today ? "bg-brand text-white" : "bg-ivory text-ink"}`}>
                <p className="text-[11px] font-medium uppercase tracking-wide opacity-80">{d.toLocaleDateString("en-GB", { weekday: "short" })}</p>
                <p className="font-heading text-lg font-bold">{d.getDate()}</p>
              </div>
              <div className="space-y-2">
                {byDay(d).map((v) => (
                  <button key={v.id} onClick={() => navigate(`/visits/${v.id}`)} data-testid={`visit-chip-${v.id}`}
                    className={`w-full rounded-lg p-2.5 text-left transition-transform hover:-translate-y-0.5 ${statusTone(v.status)}`}>
                    <p className="flex items-center gap-1 text-[11px] font-semibold"><Clock size={11} />{v.start_time}–{v.end_time}</p>
                    <p className="mt-1 flex items-center gap-1.5 truncate text-sm font-semibold text-ink">
                      {clientTag(v.client_colour) && <span className={`h-2 w-2 shrink-0 rounded-full ${clientTag(v.client_colour).dot}`} />}
                      {v.client_has_location === false && <AlertTriangle size={13} className="shrink-0 text-gold-dark" title="No home location set — check-in will be blocked until the office pins it" />}
                      <span className="truncate">{v.client_name}</span>
                    </p>
                    <p className="flex items-center gap-1.5 truncate text-xs text-ink-soft">
                      <Avatar src={photoMap[v.carer_id]} name={v.carer_name} size={18} />
                      <span className="truncate">{v.carer_name}</span>
                    </p>
                  </button>
                ))}
                {byDay(d).length === 0 && <p className="py-3 text-center text-xs text-ink-muted">—</p>}
              </div>
            </div>
          );
        })}
      </div>

      {bundleOpen && canManage && (
        <BundleBookForm clients={clients} staff={staff} defaultDate={iso(days[0])}
          onClose={() => setBundleOpen(false)} onSaved={load} />
      )}
      {open && canManage && (
        <VisitForm clients={clients} staff={staff} defaultDate={iso(days[0])}
          onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      )}
    </div>
  );
}

function VisitForm({ clients, staff, defaultDate, onClose, onSaved }) {
  const [form, setForm] = useState({ client_id: "", carer_id: "", date: defaultDate, start_time: "09:00", end_time: "10:00", services: [], service: VISIT_SERVICES[0] });
  const [hoursInfo, setHoursInfo] = useState(null);
  const [conflict, setConflict] = useState("");
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  useEffect(() => {
    if (!form.carer_id) { setHoursInfo(null); return; }
    api.get(`/staff/${form.carer_id}/hours?date=${form.date}`).then((r) => setHoursInfo(r.data)).catch(() => setHoursInfo(null));
  }, [form.carer_id, form.date]);

  const submit = async (override) => {
    try {
      await api.post("/visits", { ...form, override, tasks: form.services.map((label) => ({ label, done: false })) });
      toast.success("Visit scheduled"); onSaved();
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflict(apiError(err));
      } else toast.error(apiError(err));
    }
  };
  const save = (e) => {
    e.preventDefault();
    if (!form.client_id || !form.carer_id) { toast.error("Select a client and befriender"); return; }
    if (form.services.length === 0) { toast.error("Pick at least one activity for the visit"); return; }
    submit(false);
  };

  const over = hoursInfo && hoursInfo.allocated_hours >= hoursInfo.contracted_hours;
  const client = clients.find((c) => c.id === form.client_id);
  const selMatch = matchInfo(staff.find((s) => s.id === form.carer_id), client);
  const [goodOnly, setGoodOnly] = useState(false);
  const ranked = client
    ? [...staff].sort((a, b) => Number(matchInfo(b, client).good) - Number(matchInfo(a, client).good))
        .filter((s) => !goodOnly || matchInfo(s, client).good || s.id === form.carer_id)
    : staff;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={save} data-testid="visit-form" className="relative z-10 max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">Schedule a visit</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        <div className="space-y-4">
          <div><label className="mb-1 block text-sm font-medium text-ink">Client *</label>
            <select data-testid="visit-client-select" className={input} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div><div className="mb-1 flex items-center justify-between gap-2"><label className="block text-sm font-medium text-ink">Befriender *</label>
            {client && <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-ink-soft"><input type="checkbox" checked={goodOnly} onChange={(e) => setGoodOnly(e.target.checked)} className="accent-brand" data-testid="good-matches-toggle" /> Show good matches only</label>}</div>
            <div data-testid="befriender-picker" className="grid max-h-56 gap-2 overflow-y-auto rounded-lg border border-border p-2">
              {staff.length === 0 && <p className="p-2 text-sm text-ink-muted">No befrienders yet.</p>}
              {staff.length > 0 && ranked.length === 0 && <p className="p-2 text-sm text-ink-muted" data-testid="no-good-matches">No befrienders fully match this client. Turn off the filter to see everyone.</p>}
              {ranked.map((s) => {
                const days = (s.availability || []).filter((a) => a.available);
                const sel = form.carer_id === s.id;
                const m = matchInfo(s, client);
                return (
                  <button type="button" key={s.id} data-testid={`pick-carer-${s.id}`} onClick={() => setForm({ ...form, carer_id: s.id })}
                    className={`flex items-center gap-3 rounded-lg border p-2 text-left transition-colors ${sel ? "border-brand bg-brand-light/40 ring-1 ring-brand" : "border-border hover:bg-secondary"}`}>
                    <Avatar src={s.photo} name={s.name} size={38} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink">{s.name}</p>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {days.length === 0 ? <span className="text-[11px] text-ink-muted">No availability set</span>
                          : days.map((a) => <span key={a.day} className="rounded-full bg-sage-light px-1.5 py-0.5 text-[10px] font-medium text-sage-dark">{a.day}</span>)}
                      </div>
                      {m && (m.good
                        ? <span data-testid={`match-good-${s.id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-sage-dark"><CheckCircle2 size={12} /> Good match</span>
                        : <span data-testid={`match-warn-${s.id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-gold-dark"><AlertTriangle size={12} /> {!m.areaOk ? "Outside their areas" : "Doesn't offer all services"}</span>)}
                    </div>
                    {sel && <Check size={18} className="shrink-0 text-brand" />}
                  </button>
                );
              })}
            </div>
          </div>
          {selMatch && !selMatch.good && (
            <div data-testid="match-warning" className="rounded-lg bg-gold-light px-3 py-2 text-xs font-medium text-gold-dark">
              Heads up: {!selMatch.areaOk && `${client.area} isn't one of this befriender's areas. `}
              {selMatch.missing.length > 0 && `They haven't listed: ${selMatch.missing.join(", ")}.`} You can still schedule the visit.
            </div>
          )}
          {hoursInfo && (
            <div data-testid="carer-hours-info" className={`rounded-lg px-3 py-2 text-xs font-medium ${over ? "bg-coral-light text-coral-dark" : "bg-sage-light text-sage-dark"}`}>
              This week: {hoursInfo.allocated_hours}h allocated of {hoursInfo.contracted_hours}h contracted
              {over ? " — at or over contracted hours" : ""}
            </div>
          )}
          <div><label className="mb-1 block text-sm font-medium text-ink">Service (sets the befriender's pay) *</label>
            <select data-testid="visit-service-select" className={input} value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
              {VISIT_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className="mb-1 block text-sm font-medium text-ink">What will the befriender do? *</label>
            <div className="grid grid-cols-1 gap-1.5 rounded-lg border border-border p-3 max-h-44 overflow-y-auto scrollbar-thin" data-testid="visit-services">
              {DEFAULT_TASKS.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" data-testid={`service-${s.split(" ")[0].toLowerCase()}`} checked={form.services.includes(s)}
                    onChange={(e) => setForm((f) => ({ ...f, services: e.target.checked ? [...f.services, s] : f.services.filter((x) => x !== s) }))} />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <div><label className="mb-1 block text-sm font-medium text-ink">Date *</label>
            <UkDateInput testid="visit-date-input" clearable={false} value={form.date} onChange={(v) => v && setForm({ ...form, date: v })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="mb-1 block text-sm font-medium text-ink">Start</label>
              <input type="time" className={input} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
            <div><label className="mb-1 block text-sm font-medium text-ink">End</label>
              <input type="time" className={input} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button type="submit" data-testid="save-visit-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Schedule</button>
        </div>
        <AlertDialog open={!!conflict} onOpenChange={(o) => !o && setConflict("")}>
          <AlertDialogContent data-testid="overallocation-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Over contracted hours</AlertDialogTitle>
              <AlertDialogDescription>{conflict}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="overalloc-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="overalloc-confirm" onClick={() => { setConflict(""); submit(true); }}>Schedule anyway</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </form>
    </div>
  );
}
